import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Inbox, Briefcase, Users, CalendarCheck, Wallet, FileText, FileSpreadsheet, GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { EnquiriesSection } from "@/components/admin/EnquiriesSection";
import { JobsSection } from "@/components/admin/JobsSection";
import { EmployeesSection } from "@/components/admin/EmployeesSection";
import { AttendanceSection } from "@/components/admin/AttendanceSection";
import { PayrollSection } from "@/components/admin/PayrollSection";
import { InvoicesSection } from "@/components/admin/InvoicesSection";
import { QuotationsSection } from "@/components/admin/QuotationsSection";
import { ExamsSection } from "@/components/admin/ExamsSection";

const SECTIONS = [
  { id: "enquiries", icon: Inbox, labelKey: "secEnquiries" },
  { id: "careers", icon: Briefcase, labelKey: "secCareers" },
  { id: "employees", icon: Users, labelKey: "secEmployees" },
  { id: "attendance", icon: CalendarCheck, labelKey: "secAttendance" },
  { id: "payroll", icon: Wallet, labelKey: "secPayroll" },
  { id: "invoices", icon: FileText, labelKey: "secInvoices" },
  { id: "quotations", icon: FileSpreadsheet, labelKey: "secQuotations" },
  { id: "exams", icon: GraduationCap, labelKey: "secExams" },
];

export default function AdminDashboard() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("enquiries");

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard-container" className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Aadisarvtra logo" className="h-9 w-9 rounded-lg object-cover" />
            <div className="leading-tight">
              <h1 className="font-heading text-base font-bold">{t.admin.dashboard}</h1>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" data-testid="admin-logout-button" onClick={handleLogout} className="gap-2">
            <LogOut className="h-4 w-4" /> {t.admin.logout}
          </Button>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row">
        <aside className="flex gap-2 overflow-x-auto lg:w-52 lg:shrink-0 lg:flex-col" data-testid="admin-sidebar">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-nav-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                section === s.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <s.icon className="h-4 w-4" /> {t.admin[s.labelKey]}
            </button>
          ))}
        </aside>

        <main className="min-w-0 flex-1">
          {section === "enquiries" && <EnquiriesSection />}
          {section === "careers" && <JobsSection />}
          {section === "employees" && <EmployeesSection />}
          {section === "attendance" && <AttendanceSection />}
          {section === "payroll" && <PayrollSection />}
          {section === "invoices" && <InvoicesSection />}
          {section === "quotations" && <QuotationsSection />}
          {section === "exams" && <ExamsSection />}
        </main>
      </div>
    </div>
  );
}
